/**
 * components/landing/platform/sections/Comparison.jsx — §3.11.
 *
 * Vendor-neutral capability comparison.  Presents three approaches:
 *   1. "Multiple Tools" — the incumbent vendor sprawl.
 *   2. "Traditional Trading Software" — desktop or single-purpose.
 *   3. "Kalki Ai Cloud" — unified.
 *
 * Uses a compact matrix so buyers can scan the row that matters to
 * them.  Green check + orange partial + zinc dash for the states.
 */

import React from 'react'
import clsx from 'clsx'
import { Check, X, Minus } from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { COMPARISON } from '../data'

const CELL = (val) => {
  const v = (val || '').toLowerCase()
  if (v === 'yes')      return { label: 'Included',    tone: 'green',  Icon: Check,  strong: true }
  if (v === 'basic')    return { label: 'Basic',       tone: 'orange', Icon: Check,  strong: false }
  if (v === 'partial')  return { label: 'Partial',     tone: 'orange', Icon: Check,  strong: false }
  if (v === 'limited')  return { label: 'Limited',     tone: 'orange', Icon: Check,  strong: false }
  if (v === 'separate') return { label: 'Separate SKU', tone: 'zinc',   Icon: Minus,  strong: false }
  return { label: 'Not available', tone: 'rose', Icon: X, strong: false }
}

const TONE_CLS = {
  green:  'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  orange: 'text-brand-gold bg-brand-gold/10 border-brand-gold/30',
  zinc:   'text-zinc-400   bg-white/[0.03] border-white/10',
  rose:   'text-rose-400   bg-rose-500/10 border-rose-500/30',
}

export default function Comparison() {
  const [capH, toolsH, tradH, brandH] = COMPARISON.headers
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Platform Comparison</Eyebrow>
          <SectionHeading
            align="center"
            title={<>Three approaches. <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">One clear answer.</span></>}
            subtitle="Vendor-neutral comparison of the two common ways teams try to solve this problem — and how Kalki Ai Cloud is different."
          />
        </div>

        {/* Desktop table */}
        <div className="hidden md:block rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-xl overflow-hidden">
          <div className="grid grid-cols-4 border-b border-white/10 bg-white/[0.03]">
            <div className="px-5 py-4 text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">{capH}</div>
            <div className="px-5 py-4 text-[11px] uppercase tracking-wider text-zinc-400 font-semibold text-center">{toolsH}</div>
            <div className="px-5 py-4 text-[11px] uppercase tracking-wider text-zinc-400 font-semibold text-center">{tradH}</div>
            <div className="px-5 py-4 text-[11px] uppercase tracking-wider text-brand-gold font-semibold text-center">{brandH}</div>
          </div>
          {COMPARISON.rows.map((r, idx) => (
            <div key={r.cap} className={clsx(
              'grid grid-cols-4 border-b border-white/5 last:border-0',
              idx % 2 === 0 && 'bg-white/[0.01]',
            )}>
              <div className="px-5 py-4 text-sm text-white font-medium">{r.cap}</div>
              <ComparisonCell val={r.tools} />
              <ComparisonCell val={r.traditional} />
              <ComparisonCell val={r.brand} highlight />
            </div>
          ))}
        </div>

        {/* Mobile stacked cards */}
        <div className="md:hidden flex flex-col gap-2">
          {COMPARISON.rows.map((r) => (
            <div key={r.cap} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <div className="text-[13.5px] font-semibold text-white mb-3">{r.cap}</div>
              <div className="grid grid-cols-3 gap-2">
                <MobileCell head={toolsH}  val={r.tools} />
                <MobileCell head={tradH}   val={r.traditional} />
                <MobileCell head={brandH}   val={r.brand} highlight />
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  )
}

function ComparisonCell({ val, highlight }) {
  const c = CELL(val)
  return (
    <div className={clsx('px-5 py-4 flex items-center justify-center', highlight && 'bg-brand-gold/[0.03]')}>
      <span className={clsx(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] font-semibold',
        TONE_CLS[c.tone],
      )}>
        <c.Icon className="h-3 w-3" /> {c.label}
      </span>
    </div>
  )
}

function MobileCell({ head, val, highlight }) {
  const c = CELL(val)
  return (
    <div className={clsx('rounded-lg border border-white/10 p-2 flex flex-col items-center gap-1', highlight ? 'bg-brand-gold/[0.06] border-brand-gold/30' : 'bg-white/[0.02]')}>
      <div className="text-[9.5px] uppercase tracking-wider font-mono text-zinc-500 text-center leading-tight">{head}</div>
      <span className={clsx('inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold', TONE_CLS[c.tone])}>
        <c.Icon className="h-2.5 w-2.5" /> {c.label}
      </span>
    </div>
  )
}
